import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useRef, useState, forwardRef } from 'react';
import { useTheme } from 'styled-components';
import Lottie from 'lottie-react';
import { screenSizes } from '@libs/theme';
import { useDimensions, useOutsideClick } from '@libs/hooks';
import { DECIMAL_PRECISION_STABLE_COIN, JurisdictionsEnum, } from '@libs/backend-common';
import { getAlternativeAssetLabel } from '@libs/helpers';
import { CurrencyInputLoader } from '../content-loaders';
import { loadingAnimation } from '../animations';
import Typography from '../typography/Typography';
import { CURRENCY_INPUT_MAX_LENGTH } from '../constants';
import CurrencySelect from '../currency-select/CurrencySelect';
import { CoinsIcon } from '../assets';
import { CurrencyPercentSelector } from './CurrencyPercentSelector';
import { AnimationWrapper, CustomBalanceContainer, CustomCurrencyContainer, CustomInputTab, CustomErrorMessage, CustomInput, CustomSelectContainer, CustomTransparentButton, FlexContainer, TabsWrapper, SelectIconWrapper, SelectedIconWrapper, CustomValueWrapper, Img, FlexCenterContainer, } from './styles';
export const UNSTABLE_CURRENCY_INPUT_DECIMAL_PRECISION = 7;
const CurrencySelectInputComponent = ({ borderBottomFlat, borderTopFlat, onBtnClick, currentJurisdiction = JurisdictionsEnum.GE, buttonTitle, errorMessage, description, descLoaded, showPercentBar, className, onFocus, selectPlaceholder, onBlur, name, roundingPrecision, modalView, inputOnChange, hideErrorMessage, inputValuePlaceHolder, showSelectValue = true, ...props }, ref) => {
    const [isFocused, setFocused] = useState();
    const { colors } = useTheme();
    const inputContainer = useRef(null);
    const handleInputOnChange = (e) => {
        setFocused(true);
        onFocus?.(name);
        inputOnChange?.(e, name);
    };
    const handleInputFocusState = () => {
        setFocused(true);
        onFocus?.(name);
    };
    const handleInputOnBlur = () => {
        setFocused(false);
    };
    useOutsideClick(inputContainer, handleInputOnBlur);
    const handleClick = (e) => {
        if (!onBtnClick)
            return;
        onBtnClick(e);
    };
    const handleChange = (e) => {
        let newValue = e.target.value.replace(',', '.');
        if (newValue.length === 1 && newValue === '.') {
            newValue = '0.';
        }
        if (props.inputValue.length > newValue.length && newValue === '0.') {
            newValue = '0';
        }
        if (isNaN(+newValue) ||
            newValue.match(/^0[0-9]/) ||
            newValue.match(/[\s-+]/)) {
            return;
        }
        const currentDecimalsCount = newValue.split('.')[1]?.length;
        const defaultRoundingPrecision = props.isUnstable
            ? UNSTABLE_CURRENCY_INPUT_DECIMAL_PRECISION
            : DECIMAL_PRECISION_STABLE_COIN;
        const maxDecimals = roundingPrecision || defaultRoundingPrecision;
        if (currentDecimalsCount > maxDecimals) {
            return;
        }
        handleInputOnChange(newValue);
    };
    const loading = !(typeof descLoaded === 'boolean' ? descLoaded : true);
    const { width } = useDimensions();
    const pattern = width < screenSizes.laptop ? 'd*' : undefined;
    const inputValuePlaceholderToShow = props.selectedValue
        ? inputValuePlaceHolder
        : undefined;
    const selectRenderValue = (selectedItem) => {
        const selectedTextToDisplay = showSelectValue
            ? selectedItem?.value
            : selectedItem?.label;
        return (_jsxs(CustomValueWrapper, { placeholder: selectPlaceholder, className: "select-value__wrapper", children: [selectedItem?.icon && (_jsx(SelectedIconWrapper, { className: "select-icon", children: typeof selectedItem.icon === 'string' ? (_jsx(Img, { src: selectedItem?.icon })) : (selectedItem?.icon) })), !selectedItem?.value && (_jsx(SelectIconWrapper, { children: _jsx(CoinsIcon, {}) })), _jsx(Typography, { type: "subHeadline", fontFamily: "primaryMedium", children: selectedItem?.value !== undefined
                        ? getAlternativeAssetLabel(selectedTextToDisplay, currentJurisdiction)
                        : selectPlaceholder })] }));
    };
    return (_jsxs(CustomCurrencyContainer, { className: className, ref: inputContainer, children: [_jsxs(CustomInputTab, { isFocused: props.isLoading || isFocused, hasError: !!errorMessage, borderTopFlat: borderTopFlat, borderBottomFlat: borderBottomFlat, showPercentBar: showPercentBar, "data-testid": "CustomInputTab", children: [_jsxs(FlexCenterContainer, { children: [_jsx(CustomInput, { ref: ref, loading: !!props.isLoading, inputMode: 'decimal', type: "text", name: name, pattern: pattern, maxLength: CURRENCY_INPUT_MAX_LENGTH, onFocus: handleInputFocusState, value: props.inputValue, placeholder: props.inputPlaceHolder, title: props.title, onChange: handleChange, onWheel: (e) => e.target.blur(), isLoading: props.isLoading, onBlur: onBlur, inputValuePlaceHolder: inputValuePlaceholderToShow, hasPlaceholderOpacity: !!(isFocused || inputValuePlaceholderToShow || props.inputValue) }), props.isLoading && (_jsx(AnimationWrapper, { children: _jsx(Lottie, { loop: true, animationData: loadingAnimation }) })), loading ? (_jsx(CurrencyInputLoader, { hasButton: !!buttonTitle, hasDesc: typeof descLoaded === 'boolean' })) : (_jsxs(CustomSelectContainer, { children: [_jsxs(FlexContainer, { children: [buttonTitle && (_jsx(CustomTransparentButton, { onClick: handleClick, children: _jsx(Typography, { type: "caption", fontFamily: "primaryBold", color: colors.interactive.brandColor, children: buttonTitle }) })), _jsx(TabsWrapper, { children: _jsx(CurrencySelect, { modalView: modalView, renderValue: selectRenderValue, ...props, className: "currency-select" }) })] }), _jsx(CustomBalanceContainer, { children: description && (_jsx(Typography, { type: "caption", fontFamily: "primaryRegular", color: colors.text.default, lineHeight: 1.4, children: description })) })] }))] }), showPercentBar && (_jsx(CurrencyPercentSelector, { balance: props.balance, onChange: handleInputOnChange, roundingPrecision: roundingPrecision, inputValueMaxLength: CURRENCY_INPUT_MAX_LENGTH }))] }), !hideErrorMessage && (_jsx(CustomErrorMessage, { errorMessage: errorMessage, className: "error-message" }))] }));
};
export const CurrencySelectInput = forwardRef(CurrencySelectInputComponent);
export default CurrencySelectInput;
//# sourceMappingURL=CurrencySelectInput.js.map